import { useAppSelector } from '@/redux/hooks'
import { Button, Modal } from '@itsrever/design-system'
import Summary from '@/components/Summary'
import IntroduceIban from '@/components/IntroduceIban'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import {
    LineItemSubtypes,
    RefundPaymentMethod,
    ReturnMethod
} from '@/redux/return/returnSlice'
import { useProcess, useTranslate, usePages } from '@/hooks'
import styled from 'styled-components'
import { device, formatPrice } from '@/utils'
import TopBar from '@/components/TopBarComponent/TopBar'
import { StripeCheckout } from '@/components/Checkout/StripeCheckout'
import { ReverCard } from '@/components/ReverCard'
import { usePostHog } from '@/application/use-posthog'
import { LoadingModal } from '@/components/Loading'
import { SummaryLite } from '@/components/ReverLite/SummaryLite'
import { PbECommerceSettingsUseCaseEnum } from '@itsrever/rever-api'
import { addToast } from '@heroui/react'
import { GiftCardOffer } from '@/app/summary/gift-card-offer'
import { ModelsRefundPaymentMethod } from '@itsrever/returns-api-types'

function ConfirmationPage() {
    const { translate } = useTranslate()
    const navigate = useNavigate()
    const { t } = useTranslation()
    const { pages } = usePages()
    const { captureEventPosthog } = usePostHog()

    const clientSecret = useAppSelector(
        (store) => store.returnsApi.process.response.payment_session_id
    )

    const settings = useAppSelector(
        (store) => store.settingsApi.settings.response
    )
    const isReverLite =
        settings.use_case === PbECommerceSettingsUseCaseEnum.Lite
    const returnsApi = useAppSelector((store) => store.returnsApi)

    const isKeepYourItem = useAppSelector((store) => store.return.keepYourItem)

    const refundMethods = useAppSelector(
        (store) =>
            store.refundMethodsApi.refundMethods.response.result?.refund_methods
    )

    const selectedRefundMethod = useAppSelector(
        (store) => store.return.refundPaymentMethod
    )

    const { callProcess, loadingProcess } = useProcess()

    const returnProcess = useAppSelector((store) => store.return)

    const calculate = returnsApi.calculate.response

    useEffect(() => {
        if (!calculate.signed_summary_raw) {
            addToast({
                description: t('toast_errors.lost_order')
            })
            navigate(pages.Landing)
        }
    }, [])

    const summary = calculate.signed_summary?.return_summary

    const totalRefundAmount = summary?.total_refund_amount ?? 0
    const totalPaymentAmount = summary?.total_payment_amount ?? 0
    const RDVSurplus = summary?.line_items.find(
        (item) => item.subtype === LineItemSubtypes.RdvSurplus
    )
    const serviceFee = summary?.line_items.find(
        (item) => item.subtype === LineItemSubtypes.ServiceFee
    )
    const originalPromotionBroken = summary?.line_items?.find(
        (item) => item.subtype === LineItemSubtypes.OriginalPromotionBroken
    )
    const exchangePromotion = summary?.line_items?.find(
        (item) => item.subtype === LineItemSubtypes.ExchangePromotion
    )
    const exchangePromotionLimit = summary?.line_items?.find(
        (item) => item.subtype === LineItemSubtypes.ExchangePromotionLimit
    )
    const adjustedExchangePromotion = exchangePromotion
        ? { ...exchangePromotion }
        : undefined
    if (adjustedExchangePromotion && exchangePromotionLimit) {
        adjustedExchangePromotion.total =
            (adjustedExchangePromotion?.total ?? 0) +
            (exchangePromotionLimit?.total ?? 0)
    }

    const returnMethod = returnProcess.returnMethod
    const refundMethod =
        summary?.refund_method ?? RefundPaymentMethod.NoPaymentMethod

    const needIBAN =
        refundMethod === RefundPaymentMethod.BankTransfer &&
        totalRefundAmount > 0

    // IBAN logic
    const [isIntroduceIbanOpen, setIsIntroduceIbanOpen] =
        useState<boolean>(false)

    const orderMoneyFormat =
        useAppSelector(
            (store) => store.returnsApi.order.response?.order?.money_format
        ) ?? {}

    const handleEndProcess = () => {
        if (totalPaymentAmount > 0) {
            captureEventPosthog('Checkout', {
                Amount: -totalPaymentAmount,
                Currency: orderMoneyFormat.currency ?? 'unknown',
                ecommerceId: settings.ecommerce_id
            })
        }
        callProcess()
    }

    useEffect(() => {
        if (totalPaymentAmount > 0)
            captureEventPosthog('Confirmation - Negative Amount', {
                Amount: -totalPaymentAmount,
                Currency: orderMoneyFormat.currency ?? 'unknown',
                ecommerceId: settings.ecommerce_id
            })
    }, [])

    const giftCardMethod = refundMethods?.find(
        ({ method }) =>
            method ===
                ModelsRefundPaymentMethod.RefundPaymentMethod_GIFT_CARD ||
            method === ModelsRefundPaymentMethod.RefundPaymentMethod_PROMO_CODE
    )

    const isGiftCardOffered = giftCardMethod

    const isGiftCardRefund =
        selectedRefundMethod.method ===
            ModelsRefundPaymentMethod.RefundPaymentMethod_GIFT_CARD ||
        selectedRefundMethod.method ===
            ModelsRefundPaymentMethod.RefundPaymentMethod_PROMO_CODE

    const hasExchanges = returnProcess.newItems.length > 0

    const showGiftCardOffer =
        isGiftCardOffered && !isGiftCardRefund && !hasExchanges

    const calculatedSummary = useAppSelector(
        (store) => store.returnsApi.calculate.response.calculated_summary
    )
    const returnedItems = calculatedSummary?.returned_items ?? []
    const exchangeItems = calculatedSummary?.exchange_items ?? []
    const costs = calculatedSummary?.costs ?? []

    const isPayForLogistics = totalRefundAmount > 0 && totalPaymentAmount > 0
    const confirmationButtonText = isPayForLogistics
        ? translate('confirmation_pay_return_costs') +
          ' (' +
          formatPrice(totalPaymentAmount, orderMoneyFormat) +
          ')'
        : totalPaymentAmount > 0
          ? translate('confirmation_page_button_checkout')
          : translate('confirmation_page_button_complete')

    const displayRefundMethod =
        refundMethod !== RefundPaymentMethod.NoPaymentMethod &&
        totalRefundAmount > 0

    const logoUrl = settings.branding?.logo_url
    const title = settings.branding?.title
    const mobileLogoHeight = settings.branding?.logo_height_mobile

    const isMisako = settings.ecommerce_group === 'misako'
    const isHoff = settings.ecommerce_group === 'hoff'

    if (isHoff) {
        return (
            <>
                {loadingProcess ? <LoadingModal loading /> : null}
                <TopBar page="confirm" />
                <div className="relative mx-auto flex w-full max-w-[1000px] flex-col items-center bg-cover bg-center bg-no-repeat px-4 md:px-10">
                    <div className="mt-4 flex w-full flex-col items-start gap-3 md:mt-12">
                        <a
                            onClick={() => navigate(-1)}
                            className="flex cursor-pointer items-center gap-3"
                        >
                            <span>
                                <svg
                                    aria-hidden="true"
                                    focusable="false"
                                    fill="none"
                                    width="10"
                                    viewBox="0 0 10 10"
                                >
                                    <path
                                        d="M7 1 3 5l4 4"
                                        stroke="currentColor"
                                        strokeLinecap="square"
                                    ></path>
                                </svg>
                            </span>
                            <span className="font-haas text-[0.5em] font-light text-neutral-600">
                                {translate('top_bar_back')}
                            </span>
                        </a>
                    </div>
                    <div className="mb-8 mt-5 flex w-full flex-col gap-10 lg:flex-row lg:gap-24">
                        <div className="w-full">
                            {clientSecret ? null : (
                                <div className="flex items-center gap-6">
                                    {returnMethod !==
                                    ReturnMethod.NoReturnMethod ? (
                                        <div>
                                            <span className="font-haas text-xs text-grey-2">
                                                {translate(
                                                    'confirmation_page_return_method'
                                                )}
                                            </span>
                                            <span
                                                className="font-haas text-xs text-common-black"
                                                dangerouslySetInnerHTML={{
                                                    __html: translate(
                                                        `return_card_method${returnMethod}_title`
                                                    )
                                                }}
                                            />
                                        </div>
                                    ) : null}
                                    {displayRefundMethod && (
                                        <div>
                                            <span className="font-haas text-xs text-grey-2">
                                                {translate(
                                                    'confirmation_page_refund_method'
                                                )}
                                            </span>
                                            <span className="font-haas text-xs text-common-black">
                                                {translate(
                                                    `confirmation_page_method${refundMethod}`
                                                )}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            )}
                            <Summary
                                returnedItems={returnedItems}
                                exchangeItems={exchangeItems}
                                costs={costs}
                                totalRefundAmount={totalRefundAmount}
                                totalPaymentAmount={totalPaymentAmount}
                                RDVSurplus={RDVSurplus}
                                refundMethod={selectedRefundMethod}
                                isKeepYourItem={isKeepYourItem}
                                serviceFee={serviceFee}
                                originalPromotionBroken={
                                    originalPromotionBroken
                                }
                                exchangePromotion={adjustedExchangePromotion}
                            />

                            {!needIBAN && !clientSecret ? (
                                <div className="mt-4 flex w-full flex-col content-center items-center">
                                    <button
                                        id="confirmButton"
                                        onClick={handleEndProcess}
                                        className="ml-auto mt-8 w-fit min-w-44 cursor-pointer rounded-xl bg-black p-4 font-haas text-sm text-white disabled:cursor-not-allowed disabled:bg-neutral-500"
                                    >
                                        {confirmationButtonText}
                                    </button>
                                </div>
                            ) : null}
                        </div>
                        {clientSecret || needIBAN ? (
                            <div>
                                {clientSecret ? (
                                    <div className="min-w-80">
                                        <StripeCheckout />
                                    </div>
                                ) : null}
                                {needIBAN && (
                                    <div>
                                        <BigIbanDiv>
                                            <IntroduceIban
                                                onContinue={handleEndProcess}
                                            />
                                        </BigIbanDiv>
                                        <ButtonDiv>
                                            <div className="w-full sm:w-fit">
                                                <button
                                                    onClick={() =>
                                                        setIsIntroduceIbanOpen(
                                                            true
                                                        )
                                                    }
                                                    className="w-fit min-w-44 cursor-pointer rounded-xl bg-black p-4 font-haas text-sm text-white disabled:cursor-not-allowed disabled:bg-neutral-500"
                                                >
                                                    {translate(
                                                        'confirmation_page_button_complete'
                                                    )}
                                                </button>
                                            </div>
                                        </ButtonDiv>
                                        <CustomModal
                                            isOpen={isIntroduceIbanOpen}
                                            onRequestClose={() =>
                                                setIsIntroduceIbanOpen(false)
                                            }
                                        >
                                            <IntroduceIban
                                                onContinue={() => {
                                                    handleEndProcess()
                                                    setIsIntroduceIbanOpen(
                                                        false
                                                    )
                                                }}
                                            />
                                        </CustomModal>
                                    </div>
                                )}
                            </div>
                        ) : null}
                    </div>
                </div>
            </>
        )
    }

    if (isMisako) {
        return (
            <>
                {loadingProcess ? <LoadingModal loading /> : null}
                <div className="flex h-full flex-1 flex-col justify-center gap-4 p-4 md:px-10 md:py-6">
                    <div className="mb-auto flex w-full items-end">
                        <div className="flex w-full items-end">
                            {logoUrl ? (
                                <div className="mr-auto">
                                    <img
                                        style={
                                            {
                                                '--logo-desktop-height':
                                                    '1.5rem',
                                                '--logo-mobile-height':
                                                    mobileLogoHeight ?? '1.5rem'
                                            } as React.CSSProperties
                                        }
                                        width={285}
                                        height={70}
                                        src={logoUrl}
                                        alt={title}
                                        className="h-[var(--logo-mobile-height)] w-auto object-contain md:h-[var(--logo-desktop-height)]"
                                    />
                                </div>
                            ) : null}
                            <div className="flex items-center gap-5">
                                <a
                                    href="https://www.misako.com/pages/wishlist"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    <img
                                        src="https://www.misako.com/cdn/shop/files/favorite.svg?v=1731587455"
                                        width={20}
                                        height={20}
                                    />
                                </a>
                                <a
                                    href="https://www.misako.com/account"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    <svg
                                        width="20"
                                        height="21"
                                        viewBox="0 0 20 21"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M19 20V17.7778C19 15.3222 16.9863 13.3333 14.5 13.3333H5.5C3.01375 13.3333 1 15.3222 1 17.7778V20"
                                            stroke="rgb(21, 21, 21)"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        ></path>
                                        <path
                                            d="M10 9C12.2091 9 14 7.20914 14 5C14 2.79086 12.2091 1 10 1C7.79086 1 6 2.79086 6 5C6 7.20914 7.79086 9 10 9Z"
                                            stroke="rgb(21, 21, 21)"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        ></path>
                                    </svg>
                                </a>
                                <a
                                    href="https://www.misako.com/collections/top-ventas"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    <svg
                                        width="19"
                                        height="19"
                                        viewBox="0 0 19 19"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M13.6969 13.9469L18 18.25M15.875 8.6875C15.875 12.7951 12.5451 16.125 8.4375 16.125C4.32988 16.125 1 12.7951 1 8.6875C1 4.57988 4.32988 1.25 8.4375 1.25C12.5451 1.25 15.875 4.57988 15.875 8.6875Z"
                                            stroke="rgb(21, 21, 21)"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        ></path>
                                    </svg>
                                </a>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-1 flex-col gap-4">
                        <div className="mt-5">
                            <button
                                onClick={() => navigate(-1)}
                                className="-ml-2 mb-6 flex w-fit items-center gap-2 uppercase tracking-wide [font-family:Inter] hover:underline hover:underline-offset-8"
                            >
                                <svg
                                    width="24px"
                                    height="24px"
                                    strokeWidth="1.5"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    color="#000000"
                                    aria-hidden
                                >
                                    <path
                                        d="M15 6L9 12L15 18"
                                        stroke="#000000"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    ></path>
                                </svg>
                                Volver
                            </button>
                            <h1
                                className="text-lg font-[300] tracking-wider text-neutral-600 [font-family:Inter] lg:text-xl"
                                dangerouslySetInnerHTML={{
                                    __html: translate('progress_bar_overview')
                                }}
                            />
                        </div>

                        <div className="flex h-full flex-1 justify-between gap-16">
                            <div className="w-full flex-1">
                                <Summary
                                    returnedItems={returnedItems}
                                    exchangeItems={exchangeItems}
                                    costs={costs}
                                    totalRefundAmount={totalRefundAmount}
                                    totalPaymentAmount={totalPaymentAmount}
                                    RDVSurplus={RDVSurplus}
                                    refundMethod={selectedRefundMethod}
                                    isKeepYourItem={isKeepYourItem}
                                    serviceFee={serviceFee}
                                    originalPromotionBroken={
                                        originalPromotionBroken
                                    }
                                    exchangePromotion={
                                        adjustedExchangePromotion
                                    }
                                >
                                    {!needIBAN && !clientSecret ? (
                                        <div className="mt-8 flex w-full flex-col content-center items-center lg:mt-10">
                                            <div className="w-full">
                                                <Button
                                                    id="confirmButton"
                                                    fullWidth
                                                    onClick={handleEndProcess}
                                                >
                                                    <span className="text-[13px] font-light uppercase tracking-wider [font-family:Inter]">
                                                        {confirmationButtonText}
                                                    </span>
                                                </Button>
                                            </div>
                                        </div>
                                    ) : null}
                                    {clientSecret ? (
                                        <RightBox>
                                            <StripeCheckout />
                                        </RightBox>
                                    ) : null}
                                    {needIBAN && (
                                        <div className="mt-5 w-full flex-1 border-t border-solid border-neutral-200 pt-6">
                                            <IntroduceIban
                                                onContinue={handleEndProcess}
                                            />
                                        </div>
                                    )}
                                </Summary>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        )
    }

    return (
        <>
            {loadingProcess ? <LoadingModal loading /> : null}
            <TopBar page="confirm" />
            <GeneralDiv>
                <LeftCard>
                    <HiddenSummaryPart show={clientSecret ? 'true' : 'false'}>
                        {returnMethod !== ReturnMethod.NoReturnMethod ? (
                            <div>
                                <span className="text-xs text-grey-2">
                                    {translate(
                                        'confirmation_page_return_method'
                                    )}
                                </span>
                                <span
                                    className="text-xs text-common-black"
                                    dangerouslySetInnerHTML={{
                                        __html: translate(
                                            `return_card_method${returnMethod}_title`
                                        )
                                    }}
                                />
                            </div>
                        ) : null}
                        {displayRefundMethod && (
                            <div>
                                <span className="text-xs text-grey-2">
                                    {translate(
                                        'confirmation_page_refund_method'
                                    )}
                                </span>
                                <span className="text-xs text-common-black">
                                    {translate(
                                        `confirmation_page_method${refundMethod}`
                                    )}
                                </span>
                            </div>
                        )}
                    </HiddenSummaryPart>
                    {isReverLite ? (
                        <SummaryLite
                            returnedItems={returnedItems}
                            costs={costs}
                        />
                    ) : (
                        <Summary
                            returnedItems={returnedItems}
                            exchangeItems={exchangeItems}
                            costs={costs}
                            totalRefundAmount={totalRefundAmount}
                            totalPaymentAmount={totalPaymentAmount}
                            RDVSurplus={RDVSurplus}
                            refundMethod={selectedRefundMethod}
                            isKeepYourItem={isKeepYourItem}
                            serviceFee={serviceFee}
                            originalPromotionBroken={originalPromotionBroken}
                            exchangePromotion={adjustedExchangePromotion}
                        />
                    )}

                    {showGiftCardOffer ? (
                        <GiftCardOffer giftCard={giftCardMethod} />
                    ) : null}
                    {!needIBAN && !clientSecret ? (
                        <div className="mt-4 flex w-full flex-col content-center items-center">
                            <div className="w-full md:max-w-[280px]">
                                <Button
                                    id="confirmButton"
                                    fullWidth
                                    onClick={handleEndProcess}
                                >
                                    {confirmationButtonText}
                                </Button>
                            </div>
                        </div>
                    ) : null}
                </LeftCard>
                {clientSecret ? (
                    <RightBox>
                        <StripeCheckout />
                    </RightBox>
                ) : null}

                {needIBAN && (
                    <RightBox>
                        <BigIbanDiv>
                            <IntroduceIban onContinue={handleEndProcess} />
                        </BigIbanDiv>
                        <ButtonDiv>
                            <div className="w-full sm:w-fit">
                                <Button
                                    fullWidth
                                    onClick={() => setIsIntroduceIbanOpen(true)}
                                >
                                    {translate(
                                        'confirmation_page_button_complete'
                                    )}
                                </Button>
                            </div>
                        </ButtonDiv>
                        <CustomModal
                            isOpen={isIntroduceIbanOpen}
                            onRequestClose={() => setIsIntroduceIbanOpen(false)}
                        >
                            <IntroduceIban
                                onContinue={() => {
                                    handleEndProcess()
                                    setIsIntroduceIbanOpen(false)
                                }}
                            />
                        </CustomModal>
                    </RightBox>
                )}
            </GeneralDiv>
        </>
    )
}

const LeftCard = styled(ReverCard)`
    @media (max-width: 600px) {
        width: 100%;
        padding: 0 1rem;
    }

    @media (max-width: 899px) {
        max-width: 24rem;
    }
`

const HiddenSummaryPart = styled.div<{ show: string }>`
    align-self: flex-start;
    margin-bottom: 1rem;
    @media (max-width: 899px) {
        display: ${(p) => (p.show === 'true' ? 'none' : '')};
    }
`

const RightBox = styled.div`
    background: #ffffff;
    margin-top: 0.5rem;
    max-width: 20rem;
    z-index: 9;
    @media ${device.lg} {
        max-width: 23rem;
        margin-top: 2.5rem;
        padding: 1.5rem 2rem 1.5rem 2rem;
        box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.25);
        border-radius: 10px;
    }
`

const GeneralDiv = styled.div`
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    @media ${device.lg} {
        background-color: #f2f2f2;
        flex-direction: row;
        align-items: flex-start;
        justify-content: space-evenly;
    }
`

const CustomModal = styled(Modal)`
    // Over lg
    @media ${device.lg} {
        display: none;
    }
`

const ButtonDiv = styled.div`
    margin-top: 1.5rem;
    margin-bottom: 1rem;
    display: flex;
    justify-content: center;
    @media (max-width: 899px) {
        width: 100%;
    }
    //Over lg
    @media ${device.lg} {
        display: none;
    }
`

const BigIbanDiv = styled.div`
    //Below lg
    @media (max-width: 899px) {
        display: none;
    }
`

export default ConfirmationPage
